import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { forwardRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnknownRecord } from 'type-fest';

type LabeledValueComponentProps = Pick<LabeledValueProps, 'value' | 'valueColor' | 'valueSx' | 'valueVariant'>;

type LabeledValueProps = {
  dataTest?: string;
  label: string;
  labelColor?: TypographyProps['color'];
  labelSx?: Extract<SxProps<Theme>, UnknownRecord>;
  labelVariant?: TypographyProps['variant'];
  sx?: Extract<SxProps<Theme>, UnknownRecord>;
  value: null | string | number | undefined;
  valueColor?: TypographyProps['color'];
  valueRender?: ReactElement | ((props: LabeledValueComponentProps) => ReactElement);
  valueSx?: Extract<SxProps<Theme>, UnknownRecord>;
  valueVariant?: TypographyProps['variant'];
};

export const LabeledValue = ({
  dataTest,
  label,
  labelColor = 'textSecondary',
  labelSx,
  labelVariant = 'body2',
  sx,
  value,
  valueColor,
  valueRender = props => <LabeledValueComponentDefault {...props} />,
  valueSx,
  valueVariant = 'body2',
}: LabeledValueProps) => (
  <Stack
    data-test={dataTest}
    direction="row"
    spacing={1}
    sx={[
      {
        alignItems: 'center',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Typography color={labelColor} sx={{ wordBreak: 'keep-all', ...labelSx }} variant={labelVariant}>
      {label}:
    </Typography>{' '}
    {typeof valueRender === 'function' ? valueRender({ value, valueColor, valueVariant, valueSx }) : valueRender}
  </Stack>
);

export const LabeledValueComponentDefault = forwardRef<HTMLParagraphElement, LabeledValueComponentProps>(
  ({ value, valueColor, valueSx, valueVariant, ...rest }, ref) => {
    const { t } = useTranslation();

    return (
      <Typography
        color={valueColor}
        sx={{ wordBreak: 'keep-all', ...valueSx }}
        ref={ref}
        variant={valueVariant}
        {...rest}
      >
        {value !== null && value !== undefined ? value : t('N / A')}
      </Typography>
    );
  }
);

export const LabeledValueComponentWithInfo = ({
  tooltip,
  value,
  valueColor,
  valueSx,
  valueVariant,
}: LabeledValueComponentProps & { tooltip: string | undefined }) => (
  <Stack
    direction="row"
    sx={{
      gap: 1,
    }}
  >
    {tooltip && (
      <Tooltip placement="left" title={tooltip}>
        <IconButton size="small">
          <InfoRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}

    <LabeledValueComponentDefault value={value} valueColor={valueColor} valueSx={valueSx} valueVariant={valueVariant} />
  </Stack>
);

export const LabeledValueComponentWithTooltip = ({
  value,
  valueColor,
  valueSx,
  valueVariant,
  valueTooltip,
}: LabeledValueComponentProps & { valueTooltip?: string }) => (
  <Tooltip title={valueTooltip ?? value}>
    <LabeledValueComponentDefault value={value} valueColor={valueColor} valueSx={valueSx} valueVariant={valueVariant} />
  </Tooltip>
);
